import getConfig from 'next/config';

import {
  HOST_QUERY_KEY,
  metaApiInstance,
  ROUTE_TYPE_TYPE,
  type ServiceAppState,
} from '@kc-console/context';
import { matchPath, STATUS_CODE } from '@kc-console/core';

import { CookieKeyMap } from '../../constants/cookie';
import { commonGuardErrorHandler } from '../../utils/commonGuardErrorHandler';
import { parseCookies } from '../../utils/cookie/parseCookies';
import { getRequestUrlData } from '../../utils/getRequestUrlData';
import { parsePathname } from '../../utils/parsePathname';
import { getServerTraceHttpTimingOptions } from '../../utils/serverTrace/store';

import type { Guard } from '../../types';

/**
 * MegaMenu와 현재 서비스의 MetaData를 가져옵니다.
 *
 * @description
 * - GNB, MegaMenu에서 표현할 데이터를 MetaAPI로 부터 받아오는 가드입니다.
 */
export const determineMegaMenuGuard: Guard<ServiceAppState> = async ({
  context,
  state,
  queryClient,
}) => {
  const { region, token = '', language } = state;
  const { urlObj, cookie } = getRequestUrlData(context);
  const { serviceKeyword, menuPath, routePath } = parsePathname(urlObj.pathname);
  const cookies = parseCookies(cookie || '');
  const cookieToken = cookies[CookieKeyMap.TOKEN_KEY] || '';

  const { fetchMegaMenu, fetchServiceMeta } = metaApiInstance({
    token: token || cookieToken,
    ...getServerTraceHttpTimingOptions(context.req, 'config'),
    overrideAxiosRequestConfig: {
      headers: {
        Cookie: `KC_LANG=${language}`,
      },
    },
  });

  try {
    const categories = await queryClient.fetchQuery({
      queryKey: [HOST_QUERY_KEY.GET_MEGA_MENU, region ? { language, region } : { language }],
      queryFn: () => fetchMegaMenu({ region }),
    });

    const servicePath = `/${serviceKeyword}`;

    const matchedService = categories
      .flatMap(({ services }) => services)
      .find(service => service.path === servicePath);

    if (!matchedService) {
      return { status: STATUS_CODE.FORBIDDEN_REGION };
    }

    const serviceId = matchedService.id;
    const serviceMeta = await queryClient.fetchQuery({
      queryKey: [
        HOST_QUERY_KEY.GET_SERVICE_META,
        region ? { serviceId, language, region } : { serviceId, language },
      ],
      queryFn: () => fetchServiceMeta({ serviceId, region }),
    });

    // categories[].services[]에 있지만, serviceMeta조회 응답이 없는 경우
    // 어드민에서 처리가 잘못된 것으로 간주하여 500 에러 처리한다.
    if (!serviceMeta) {
      return { status: STATUS_CODE.INTERNAL_SERVER_ERROR };
    }

    /**
     * 로컬 개발 편의를 위한 스킵 코드.
     * 로컬에서 서비스 개발 시 serviceMeta.route를 등록하지 않고 개발할 수 있도록 가드를 통과시키는 설정.
     */
    if (process.env.NODE_ENV === 'development' && !getConfig()?.serverRuntimeConfig?.isBaguni) {
      return { status: STATUS_CODE.OK };
    }

    const matchedRoute = serviceMeta.routes.find(route => matchPath(routePath, route.path));
    if (!matchedRoute) {
      return { status: STATUS_CODE.NOT_FOUND_ROUTE };
    }

    if (
      matchedRoute.routeType === ROUTE_TYPE_TYPE.REDIRECT ||
      matchedRoute.routeType === ROUTE_TYPE_TYPE.STANDALONE
    ) {
      return { status: STATUS_CODE.OK };
    }

    const flatMenus = serviceMeta.menus.flatMap(({ subMenus, ...menu }) => [menu, ...subMenus]);
    const matchedMenu = flatMenus.find(menu => menu.path === menuPath);
    if (!matchedMenu) {
      return { status: STATUS_CODE.FORBIDDEN_REGION_SUB };
    }

    return { status: STATUS_CODE.OK };
  } catch (error) {
    return commonGuardErrorHandler({
      context,
      state,
      error,
    });
  }
};

determineMegaMenuGuard.displayName = 'determineMegaMenuGuard';
